/**
 * Proof Quest - Logical Deduction as Detective Work
 * 
 * "You're not checking your arithmetic. You're building an airtight case."
 * 
 * Key distinction:
 * - VERIFY: Plug values back in, confirm answer is correct (arithmetic check)
 * - PROVE: Chain of logical steps where each follows necessarily from previous
 * 
 * Framing: Detective/Lawyer building a case
 * - Evidence = Givens
 * - Rules = Theorems, Postulates, Definitions
 * - Case = The Proof
 * - Arguments = Steps with justifications
 */

// ============================================================================
// TYPES
// ============================================================================

export type ProofLevel = 
  | 'intro'           // Identify givens, match reasons to steps
  | 'guided'          // Fill in missing steps or reasons
  | 'scaffolded'      // Arrange steps in order
  | 'independent'     // Construct proof with hints available
  | 'mastery';        // Construct proof from scratch

export type ProofDomain = 
  | 'logic'           // Pure logical reasoning (if-then, contrapositive)
  | 'algebra'         // Algebraic proofs (properties of equality)
  | 'geometry'        // Geometric proofs (triangles, parallel lines, etc.)
  | 'number_theory';  // Divisibility, even/odd, etc.

export type ProofLens = 
  | 'identify_givens'     // "What do we know for sure?"
  | 'identify_goal'       // "What are we trying to prove?"
  | 'match_reasons'       // Match statements to justifications
  | 'find_flaw'           // Find the logical error
  | 'next_step'           // "What can we conclude next?"
  | 'fill_gaps'           // Complete missing steps
  | 'arrange_steps'       // Put scrambled proof in order
  | 'justify_step'        // Provide the reason for a given step
  | 'construct_proof';    // Build the whole thing

export interface ProofRule {
  id: string;
  name: string;
  shortName: string;
  category: 'postulate' | 'theorem' | 'definition' | 'property';
  statement: string;
  example?: string;
  domain: ProofDomain[];
}

export interface ProofStep {
  id: string;
  statement: string;
  reason: string;
  reasonId: string;        // Links to ProofRule
  dependsOn: string[];     // IDs of previous steps this follows from
}

export interface ProofProblem {
  id: string;
  title: string;
  domain: ProofDomain;
  level: ProofLevel;
  
  // The setup
  scenario: string;        // Detective story framing
  diagram?: string;        // SVG or description
  
  // Given information
  givens: Array<{
    id: string;
    statement: string;
    label?: string;        // e.g., "Evidence A"
  }>;
  
  // What we're proving
  goal: {
    statement: string;
    label?: string;        // e.g., "The Verdict"
  };
  
  // The correct proof
  solution: ProofStep[];
  
  // Available rules for this proof
  availableRules: string[];  // Rule IDs
  
  // Hints (progressive)
  hints: string[];
  
  // XP and difficulty
  xpReward: number;
  estimatedMinutes: number;
}

export interface ProofAttempt {
  odId: string;
  steps: Array<{
    statement: string;
    reason: string;
    isCorrect?: boolean;
    feedback?: string;
  }>;
}

// ============================================================================
// PROOF RULES LIBRARY
// ============================================================================

export const PROOF_RULES: Record<string, ProofRule> = {
  // ---- Properties of Equality (Algebra) ----
  'reflexive': {
    id: 'reflexive',
    name: 'Reflexive Property of Equality',
    shortName: 'Reflexive',
    category: 'property',
    statement: 'Any quantity is equal to itself: a = a',
    example: 'x = x',
    domain: ['algebra', 'geometry'],
  },
  'symmetric': {
    id: 'symmetric',
    name: 'Symmetric Property of Equality',
    shortName: 'Symmetric',
    category: 'property',
    statement: 'If a = b, then b = a',
    example: 'If x = 5, then 5 = x',
    domain: ['algebra', 'geometry'],
  },
  'transitive': {
    id: 'transitive',
    name: 'Transitive Property of Equality',
    shortName: 'Transitive',
    category: 'property',
    statement: 'If a = b and b = c, then a = c',
    example: 'If x = y and y = 3, then x = 3',
    domain: ['algebra', 'geometry'],
  },
  'substitution': {
    id: 'substitution',
    name: 'Substitution Property',
    shortName: 'Substitution',
    category: 'property',
    statement: 'If a = b, then a can replace b in any expression',
    example: 'If x = 3, then 2x = 2(3) = 6',
    domain: ['algebra', 'geometry'],
  },
  'addition_prop': {
    id: 'addition_prop',
    name: 'Addition Property of Equality',
    shortName: 'Add. Prop. =',
    category: 'property',
    statement: 'If a = b, then a + c = b + c',
    example: 'If x - 3 = 7, then x - 3 + 3 = 7 + 3',
    domain: ['algebra'],
  },
  'subtraction_prop': {
    id: 'subtraction_prop',
    name: 'Subtraction Property of Equality',
    shortName: 'Sub. Prop. =',
    category: 'property',
    statement: 'If a = b, then a - c = b - c',
    example: 'If x + 5 = 12, then x + 5 - 5 = 12 - 5',
    domain: ['algebra'],
  },
  'multiplication_prop': {
    id: 'multiplication_prop',
    name: 'Multiplication Property of Equality',
    shortName: 'Mult. Prop. =',
    category: 'property',
    statement: 'If a = b, then a × c = b × c',
    example: 'If x/2 = 4, then (x/2) × 2 = 4 × 2',
    domain: ['algebra'],
  },
  'division_prop': {
    id: 'division_prop',
    name: 'Division Property of Equality',
    shortName: 'Div. Prop. =',
    category: 'property',
    statement: 'If a = b and c ≠ 0, then a/c = b/c',
    example: 'If 3x = 12, then 3x/3 = 12/3',
    domain: ['algebra'],
  },
  'distributive': {
    id: 'distributive',
    name: 'Distributive Property',
    shortName: 'Distributive',
    category: 'property',
    statement: 'a(b + c) = ab + ac',
    example: '3(x + 2) = 3x + 6',
    domain: ['algebra'],
  },
  
  // ---- Logic Rules ----
  'given': {
    id: 'given',
    name: 'Given',
    shortName: 'Given',
    category: 'postulate',
    statement: 'Information stated in the problem',
    domain: ['logic', 'algebra', 'geometry', 'number_theory'],
  },
  'definition': {
    id: 'definition',
    name: 'Definition',
    shortName: 'Def.',
    category: 'definition',
    statement: 'The meaning of a term',
    domain: ['logic', 'algebra', 'geometry', 'number_theory'],
  },
  'modus_ponens': {
    id: 'modus_ponens',
    name: 'Modus Ponens (Law of Detachment)',
    shortName: 'Modus Ponens',
    category: 'theorem',
    statement: 'If P → Q is true, and P is true, then Q is true',
    example: 'If "it rains → ground is wet" and "it rains", then "ground is wet"',
    domain: ['logic'],
  },
  'modus_tollens': {
    id: 'modus_tollens',
    name: 'Modus Tollens (Contrapositive)',
    shortName: 'Modus Tollens',
    category: 'theorem',
    statement: 'If P → Q is true, and Q is false, then P is false',
    example: 'If "it rains → ground is wet" and "ground is NOT wet", then "it did NOT rain"',
    domain: ['logic'],
  },
  'law_syllogism': {
    id: 'law_syllogism',
    name: 'Law of Syllogism',
    shortName: 'Syllogism',
    category: 'theorem',
    statement: 'If P → Q and Q → R, then P → R',
    example: 'If "rain → wet" and "wet → slippery", then "rain → slippery"',
    domain: ['logic'],
  },
  
  // ---- Geometry: Angles ----
  'angle_addition': {
    id: 'angle_addition',
    name: 'Angle Addition Postulate',
    shortName: 'Angle Add. Post.',
    category: 'postulate',
    statement: 'If point B is in the interior of ∠AOC, then m∠AOB + m∠BOC = m∠AOC',
    domain: ['geometry'],
  },
  'vertical_angles': {
    id: 'vertical_angles',
    name: 'Vertical Angles Theorem',
    shortName: 'Vert. ∠s ≅',
    category: 'theorem',
    statement: 'Vertical angles are congruent',
    domain: ['geometry'],
  },
  'linear_pair': {
    id: 'linear_pair',
    name: 'Linear Pair Postulate',
    shortName: 'Linear Pair',
    category: 'postulate',
    statement: 'If two angles form a linear pair, they are supplementary (sum to 180°)',
    domain: ['geometry'],
  },
  'complement_def': {
    id: 'complement_def',
    name: 'Definition of Complementary Angles',
    shortName: 'Def. Comp. ∠s',
    category: 'definition',
    statement: 'Two angles are complementary if their measures sum to 90°',
    domain: ['geometry'],
  },
  'supplement_def': {
    id: 'supplement_def',
    name: 'Definition of Supplementary Angles',
    shortName: 'Def. Supp. ∠s',
    category: 'definition',
    statement: 'Two angles are supplementary if their measures sum to 180°',
    domain: ['geometry'],
  },
  
  // ---- Geometry: Parallel Lines ----
  'corresponding_angles': {
    id: 'corresponding_angles',
    name: 'Corresponding Angles Postulate',
    shortName: 'Corr. ∠s ≅',
    category: 'postulate',
    statement: 'If two parallel lines are cut by a transversal, corresponding angles are congruent',
    domain: ['geometry'],
  },
  'alternate_interior': {
    id: 'alternate_interior',
    name: 'Alternate Interior Angles Theorem',
    shortName: 'Alt. Int. ∠s ≅',
    category: 'theorem',
    statement: 'If two parallel lines are cut by a transversal, alternate interior angles are congruent',
    domain: ['geometry'],
  },
  'alternate_exterior': {
    id: 'alternate_exterior',
    name: 'Alternate Exterior Angles Theorem',
    shortName: 'Alt. Ext. ∠s ≅',
    category: 'theorem',
    statement: 'If two parallel lines are cut by a transversal, alternate exterior angles are congruent',
    domain: ['geometry'],
  },
  'same_side_interior': {
    id: 'same_side_interior',
    name: 'Same-Side Interior Angles Theorem',
    shortName: 'Same-Side Int. ∠s Supp.',
    category: 'theorem',
    statement: 'If two parallel lines are cut by a transversal, same-side interior angles are supplementary',
    domain: ['geometry'],
  },
  
  // ---- Geometry: Triangles ----
  'triangle_sum': {
    id: 'triangle_sum',
    name: 'Triangle Sum Theorem',
    shortName: 'Δ Sum Thm.',
    category: 'theorem',
    statement: 'The sum of the measures of the angles of a triangle is 180°',
    domain: ['geometry'],
  },
  'isosceles_base_angles': {
    id: 'isosceles_base_angles',
    name: 'Isosceles Triangle Theorem',
    shortName: 'Isos. Δ Thm.',
    category: 'theorem',
    statement: 'If two sides of a triangle are congruent, then the angles opposite them are congruent',
    domain: ['geometry'],
  },
  'exterior_angle': {
    id: 'exterior_angle',
    name: 'Exterior Angle Theorem',
    shortName: 'Ext. ∠ Thm.',
    category: 'theorem',
    statement: 'The measure of an exterior angle equals the sum of the two non-adjacent interior angles',
    domain: ['geometry'],
  },
  
  // ---- Congruence ----
  'sss': {
    id: 'sss',
    name: 'Side-Side-Side Congruence',
    shortName: 'SSS ≅',
    category: 'postulate',
    statement: 'If three sides of one triangle are congruent to three sides of another, the triangles are congruent',
    domain: ['geometry'],
  },
  'sas': {
    id: 'sas',
    name: 'Side-Angle-Side Congruence',
    shortName: 'SAS ≅',
    category: 'postulate',
    statement: 'If two sides and the included angle of one triangle are congruent to those of another, the triangles are congruent',
    domain: ['geometry'],
  },
  'asa': {
    id: 'asa',
    name: 'Angle-Side-Angle Congruence',
    shortName: 'ASA ≅',
    category: 'postulate',
    statement: 'If two angles and the included side of one triangle are congruent to those of another, the triangles are congruent',
    domain: ['geometry'],
  },
  'aas': {
    id: 'aas',
    name: 'Angle-Angle-Side Congruence',
    shortName: 'AAS ≅',
    category: 'theorem',
    statement: 'If two angles and a non-included side of one triangle are congruent to those of another, the triangles are congruent',
    domain: ['geometry'],
  },
  'cpctc': {
    id: 'cpctc',
    name: 'Corresponding Parts of Congruent Triangles are Congruent',
    shortName: 'CPCTC',
    category: 'theorem',
    statement: 'If two triangles are congruent, all their corresponding parts are congruent',
    domain: ['geometry'],
  },
  
  // ---- Number Theory ----
  'even_def': {
    id: 'even_def',
    name: 'Definition of Even Number',
    shortName: 'Def. Even',
    category: 'definition',
    statement: 'An integer n is even if n = 2k for some integer k',
    domain: ['number_theory', 'algebra'],
  },
  'odd_def': {
    id: 'odd_def',
    name: 'Definition of Odd Number',
    shortName: 'Def. Odd',
    category: 'definition',
    statement: 'An integer n is odd if n = 2k + 1 for some integer k',
    domain: ['number_theory', 'algebra'],
  },
  'divisibility_def': {
    id: 'divisibility_def',
    name: 'Definition of Divisibility',
    shortName: 'Def. Divisible',
    category: 'definition',
    statement: 'a divides b (a|b) if b = ak for some integer k',
    domain: ['number_theory'],
  },
};

// ============================================================================
// SAMPLE PROOF PROBLEMS
// ============================================================================

export const PROOF_PROBLEMS: ProofProblem[] = [
  // ---- INTRO LEVEL: Logic ----
  {
    id: 'logic_intro_1',
    title: 'The Stolen Cookie Case',
    domain: 'logic',
    level: 'intro',
    scenario: `🕵️ Detective Case #1: The Cookie Jar Mystery
    
You're a detective investigating who ate the cookies. You've gathered evidence and know the house rules. Can you prove who did it?`,
    givens: [
      { id: 'g1', statement: 'If someone has crumbs on their shirt, they ate cookies', label: 'House Rule A' },
      { id: 'g2', statement: 'Max has crumbs on his shirt', label: 'Evidence B' },
    ],
    goal: {
      statement: 'Max ate the cookies',
      label: 'The Verdict',
    },
    solution: [
      {
        id: 's1',
        statement: 'If someone has crumbs on their shirt, they ate cookies',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: 'Max has crumbs on his shirt',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's3',
        statement: 'Max ate the cookies',
        reason: 'Modus Ponens (from House Rule A and Evidence B)',
        reasonId: 'modus_ponens',
        dependsOn: ['s1', 's2'],
      },
    ],
    availableRules: ['given', 'modus_ponens', 'modus_tollens'],
    hints: [
      'Look at the house rule - it\'s an "if... then..." statement',
      'Evidence B tells us the "if" part is true for Max',
      'When the "if" is true, what can we conclude about the "then"?',
    ],
    xpReward: 25,
    estimatedMinutes: 3,
  },
  
  {
    id: 'logic_intro_2',
    title: 'The Dry Sidewalk Mystery',
    domain: 'logic',
    level: 'intro',
    scenario: `🕵️ Detective Case #2: Did It Rain?
    
You arrive at a scene and need to figure out if it rained earlier. You know how weather works, and you can see the sidewalk.`,
    givens: [
      { id: 'g1', statement: 'If it rains, then the sidewalk gets wet', label: 'Weather Rule' },
      { id: 'g2', statement: 'The sidewalk is NOT wet', label: 'Your Observation' },
    ],
    goal: {
      statement: 'It did NOT rain',
      label: 'Your Conclusion',
    },
    solution: [
      {
        id: 's1',
        statement: 'If it rains, then the sidewalk gets wet',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: 'The sidewalk is NOT wet',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's3',
        statement: 'It did NOT rain',
        reason: 'Modus Tollens (contrapositive reasoning)',
        reasonId: 'modus_tollens',
        dependsOn: ['s1', 's2'],
      },
    ],
    availableRules: ['given', 'modus_ponens', 'modus_tollens'],
    hints: [
      'This is different from the cookie case - we know the "then" part is FALSE',
      'If rain → wet sidewalk, but sidewalk is dry, what does that tell us?',
      'This is called "contrapositive" reasoning - if the result didn\'t happen, the cause didn\'t either',
    ],
    xpReward: 30,
    estimatedMinutes: 4,
  },
  
  // ---- GUIDED LEVEL: Algebra ----
  {
    id: 'algebra_guided_1',
    title: 'The Equation Escape Room',
    domain: 'algebra',
    level: 'guided',
    scenario: `🔐 Escape Room Challenge: Crack the Code
    
To escape, you must PROVE that x = 7. Not just find x - PROVE it with justified steps. Each step must follow logically from the previous one.`,
    givens: [
      { id: 'g1', statement: '2x + 3 = 17', label: 'The Lock Combination' },
    ],
    goal: {
      statement: 'x = 7',
      label: 'The Escape Code',
    },
    solution: [
      {
        id: 's1',
        statement: '2x + 3 = 17',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: '2x + 3 - 3 = 17 - 3',
        reason: 'Subtraction Property of Equality',
        reasonId: 'subtraction_prop',
        dependsOn: ['s1'],
      },
      {
        id: 's3',
        statement: '2x = 14',
        reason: 'Simplification',
        reasonId: 'definition',
        dependsOn: ['s2'],
      },
      {
        id: 's4',
        statement: '2x / 2 = 14 / 2',
        reason: 'Division Property of Equality',
        reasonId: 'division_prop',
        dependsOn: ['s3'],
      },
      {
        id: 's5',
        statement: 'x = 7',
        reason: 'Simplification',
        reasonId: 'definition',
        dependsOn: ['s4'],
      },
    ],
    availableRules: ['given', 'addition_prop', 'subtraction_prop', 'multiplication_prop', 'division_prop', 'definition', 'substitution'],
    hints: [
      'To isolate x, first get rid of the +3 on the left side',
      'Whatever you do to one side, you must do to the other - which property is that?',
      'After subtracting 3, you\'ll have 2x = 14. Now how do you get x alone?',
    ],
    xpReward: 35,
    estimatedMinutes: 5,
  },
  
  // ---- SCAFFOLDED LEVEL: Arrange steps ----
  {
    id: 'algebra_scaffolded_1',
    title: 'The Scrambled Evidence',
    domain: 'algebra',
    level: 'scaffolded',
    scenario: `📋 Case File Reconstruction
    
A gust of wind scattered your proof! The steps are all here, but in the wrong order. Arrange them to build a valid proof.`,
    givens: [
      { id: 'g1', statement: '3(x - 2) = 15', label: 'Starting Equation' },
    ],
    goal: {
      statement: 'x = 7',
      label: 'Final Answer',
    },
    solution: [
      {
        id: 's1',
        statement: '3(x - 2) = 15',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: '3x - 6 = 15',
        reason: 'Distributive Property',
        reasonId: 'distributive',
        dependsOn: ['s1'],
      },
      {
        id: 's3',
        statement: '3x - 6 + 6 = 15 + 6',
        reason: 'Addition Property of Equality',
        reasonId: 'addition_prop',
        dependsOn: ['s2'],
      },
      {
        id: 's4',
        statement: '3x = 21',
        reason: 'Simplification',
        reasonId: 'definition',
        dependsOn: ['s3'],
      },
      {
        id: 's5',
        statement: 'x = 7',
        reason: 'Division Property of Equality',
        reasonId: 'division_prop',
        dependsOn: ['s4'],
      },
    ],
    availableRules: ['given', 'distributive', 'addition_prop', 'division_prop', 'definition'],
    hints: [
      'Start with what\'s Given - that\'s always step 1',
      'What do you do first with 3(x - 2)? Distribute!',
      'After distributing, you need to get x alone...',
    ],
    xpReward: 40,
    estimatedMinutes: 5,
  },
  
  // ---- INDEPENDENT LEVEL: Geometry ----
  {
    id: 'geometry_independent_1',
    title: 'The Parallel Lines Conspiracy',
    domain: 'geometry',
    level: 'independent',
    scenario: `🔍 Geometry Detective Agency
    
Two streets run parallel to each other. A third street crosses both of them. Some angles are marked. Can you prove what we suspect?

    Street A  ═══════╱═══════
                    ╱ 1  2
                   ╱ 3  4
    Street B  ════╱══════════
                 ╱ 5  6
                ╱ 7  8
              Main St`,
    givens: [
      { id: 'g1', statement: 'Street A ∥ Street B', label: 'City Map Shows' },
      { id: 'g2', statement: 'Main St is a transversal', label: 'Visual Evidence' },
      { id: 'g3', statement: 'm∠1 = 120°', label: 'Measured Angle' },
    ],
    goal: {
      statement: 'm∠5 = 120°',
      label: 'Prove This',
    },
    solution: [
      {
        id: 's1',
        statement: 'Street A ∥ Street B',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: 'Main St is a transversal cutting parallel lines',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's3',
        statement: 'm∠1 = 120°',
        reason: 'Given',
        reasonId: 'given',
        dependsOn: [],
      },
      {
        id: 's4',
        statement: '∠1 ≅ ∠5 (they are corresponding angles)',
        reason: 'Corresponding Angles Postulate',
        reasonId: 'corresponding_angles',
        dependsOn: ['s1', 's2'],
      },
      {
        id: 's5',
        statement: 'm∠1 = m∠5',
        reason: 'Definition of Congruent Angles',
        reasonId: 'definition',
        dependsOn: ['s4'],
      },
      {
        id: 's6',
        statement: 'm∠5 = 120°',
        reason: 'Substitution Property',
        reasonId: 'substitution',
        dependsOn: ['s3', 's5'],
      },
    ],
    availableRules: ['given', 'corresponding_angles', 'alternate_interior', 'alternate_exterior', 'definition', 'substitution', 'transitive'],
    hints: [
      'Look at where ∠1 and ∠5 are positioned - same position at each intersection',
      'What do we call angles in the same position when a transversal cuts parallel lines?',
      'Once you know the angles are congruent, how do you connect that to the actual degree measure?',
    ],
    xpReward: 50,
    estimatedMinutes: 7,
  },
  
  // ---- Number Theory ----
  {
    id: 'number_theory_guided_1',
    title: 'The Even Sum Theorem',
    domain: 'number_theory',
    level: 'guided',
    scenario: `🔢 Number Detective Division
    
Your partner claims: "The sum of any two even numbers is always even." 
Is this true? Don't just check examples - PROVE it works for ALL even numbers!`,
    givens: [
      { id: 'g1', statement: 'Let a and b be any two even numbers', label: 'Setup' },
      { id: 'g2', statement: 'a = 2m for some integer m (definition of even)', label: 'What "even" means for a' },
      { id: 'g3', statement: 'b = 2n for some integer n (definition of even)', label: 'What "even" means for b' },
    ],
    goal: {
      statement: 'a + b is even',
      label: 'Prove This',
    },
    solution: [
      {
        id: 's1',
        statement: 'a = 2m for some integer m',
        reason: 'Definition of even number',
        reasonId: 'even_def',
        dependsOn: [],
      },
      {
        id: 's2',
        statement: 'b = 2n for some integer n',
        reason: 'Definition of even number',
        reasonId: 'even_def',
        dependsOn: [],
      },
      {
        id: 's3',
        statement: 'a + b = 2m + 2n',
        reason: 'Substitution',
        reasonId: 'substitution',
        dependsOn: ['s1', 's2'],
      },
      {
        id: 's4',
        statement: 'a + b = 2(m + n)',
        reason: 'Distributive Property (factoring)',
        reasonId: 'distributive',
        dependsOn: ['s3'],
      },
      {
        id: 's5',
        statement: '(m + n) is an integer',
        reason: 'Integers are closed under addition',
        reasonId: 'definition',
        dependsOn: [],
      },
      {
        id: 's6',
        statement: 'a + b = 2k where k = (m + n) is an integer',
        reason: 'Substitution (let k = m + n)',
        reasonId: 'substitution',
        dependsOn: ['s4', 's5'],
      },
      {
        id: 's7',
        statement: 'a + b is even',
        reason: 'Definition of even number',
        reasonId: 'even_def',
        dependsOn: ['s6'],
      },
    ],
    availableRules: ['given', 'even_def', 'odd_def', 'substitution', 'distributive', 'definition'],
    hints: [
      'Use the definition: an even number can be written as 2 times some integer',
      'Add the two expressions together - what do you get?',
      'Can you factor out a 2? If a + b = 2 × (something), and that something is an integer...',
    ],
    xpReward: 45,
    estimatedMinutes: 8,
  },
];

// ============================================================================
// PROOF GENERATION & VALIDATION
// ============================================================================

/**
 * Get problems filtered by domain and level
 */
export function getProofProblems(params: {
  domain?: ProofDomain;
  level?: ProofLevel;
  limit?: number;
}): ProofProblem[] {
  let problems = [...PROOF_PROBLEMS];
  
  if (params.domain) {
    problems = problems.filter(p => p.domain === params.domain);
  }
  
  if (params.level) {
    problems = problems.filter(p => p.level === params.level);
  }
  
  if (params.limit) {
    problems = problems.slice(0, params.limit);
  }
  
  return problems;
}

/**
 * Get available rules for a problem, with full details
 */
export function getRulesForProblem(problemId: string): ProofRule[] {
  const problem = PROOF_PROBLEMS.find(p => p.id === problemId);
  if (!problem) return [];
  
  return problem.availableRules
    .map(ruleId => PROOF_RULES[ruleId])
    .filter(Boolean);
}

/**
 * Validate a single proof step
 */
export function validateStep(
  step: { statement: string; reason: string },
  problem: ProofProblem,
  previousSteps: ProofStep[]
): { valid: boolean; feedback: string } {
  // Check if statement matches any solution step
  const matchingStep = problem.solution.find(
    s => normalizeStatement(s.statement) === normalizeStatement(step.statement)
  );
  
  if (!matchingStep) {
    return {
      valid: false,
      feedback: 'This statement doesn\'t follow from what we know. Check your logic!',
    };
  }
  
  // Check if reason is correct
  const reasonRule = Object.values(PROOF_RULES).find(
    r => r.name.toLowerCase().includes(step.reason.toLowerCase()) ||
         r.shortName.toLowerCase().includes(step.reason.toLowerCase()) ||
         r.id === step.reason.toLowerCase()
  );
  
  if (!reasonRule || reasonRule.id !== matchingStep.reasonId) {
    return {
      valid: false,
      feedback: `The statement is correct, but that's not the right reason. What property or theorem justifies this step?`,
    };
  }
  
  // Check if dependencies are met
  for (const depId of matchingStep.dependsOn) {
    const depStep = problem.solution.find(s => s.id === depId);
    if (depStep) {
      const found = previousSteps.some(
        ps => normalizeStatement(ps.statement) === normalizeStatement(depStep.statement)
      );
      if (!found) {
        return {
          valid: false,
          feedback: 'You\'re skipping a step! This conclusion requires something you haven\'t established yet.',
        };
      }
    }
  }
  
  return {
    valid: true,
    feedback: '✓ Solid reasoning, detective!',
  };
}

/**
 * Check if a complete proof is valid
 */
export function validateProof(
  attempt: ProofAttempt,
  problem: ProofProblem
): { 
  valid: boolean; 
  score: number; 
  feedback: string;
  stepResults: Array<{ valid: boolean; feedback: string }>;
} {
  const stepResults: Array<{ valid: boolean; feedback: string }> = [];
  const validSteps: ProofStep[] = [];
  
  for (let i = 0; i < attempt.steps.length; i++) {
    const result = validateStep(attempt.steps[i], problem, validSteps);
    stepResults.push(result);
    
    if (result.valid) {
      const matchingStep = problem.solution.find(
        s => normalizeStatement(s.statement) === normalizeStatement(attempt.steps[i].statement)
      );
      if (matchingStep) {
        validSteps.push(matchingStep);
      }
    }
  }
  
  // Check if we reached the goal
  const goalReached = validSteps.some(
    s => normalizeStatement(s.statement) === normalizeStatement(problem.goal.statement)
  );
  
  const validCount = stepResults.filter(r => r.valid).length;
  const score = validCount / problem.solution.length;
  
  return {
    valid: goalReached && validCount === attempt.steps.length,
    score,
    feedback: goalReached 
      ? '🎉 Case closed! Your proof is airtight!'
      : validCount > 0
      ? `Good progress! You've established ${validCount} valid steps. Keep going!`
      : 'Let\'s start from the beginning. What do we know for sure?',
    stepResults,
  };
}

function normalizeStatement(s: string): string {
  return s.toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[°]/g, ' degrees')
    .trim();
}

// ============================================================================
// PROOF LEVEL PROGRESSION
// ============================================================================

export const PROOF_LEVEL_INFO: Record<ProofLevel, {
  name: string;
  description: string;
  xpMultiplier: number;
  unlockRequirement: string;
}> = {
  intro: {
    name: 'Rookie Detective',
    description: 'Identify evidence and match it to conclusions',
    xpMultiplier: 1.0,
    unlockRequirement: 'Start here!',
  },
  guided: {
    name: 'Junior Investigator',
    description: 'Fill in missing steps with guidance',
    xpMultiplier: 1.3,
    unlockRequirement: 'Complete 3 intro proofs',
  },
  scaffolded: {
    name: 'Case Detective',
    description: 'Arrange scrambled evidence in logical order',
    xpMultiplier: 1.5,
    unlockRequirement: 'Complete 3 guided proofs',
  },
  independent: {
    name: 'Lead Detective',
    description: 'Build proofs with hints available',
    xpMultiplier: 2.0,
    unlockRequirement: 'Complete 3 scaffolded proofs',
  },
  mastery: {
    name: 'Chief Inspector',
    description: 'Construct proofs from scratch',
    xpMultiplier: 2.5,
    unlockRequirement: 'Complete 5 independent proofs',
  },
};

export const PROOF_DOMAIN_INFO: Record<ProofDomain, {
  name: string;
  emoji: string;
  description: string;
  color: string;
}> = {
  logic: {
    name: 'Logical Reasoning',
    emoji: '🧠',
    description: 'If-then statements, contrapositive, syllogisms',
    color: '#a855f7',
  },
  algebra: {
    name: 'Algebraic Proofs',
    emoji: '🔢',
    description: 'Properties of equality, equation solving',
    color: '#3b82f6',
  },
  geometry: {
    name: 'Geometric Proofs',
    emoji: '📐',
    description: 'Angles, triangles, parallel lines, congruence',
    color: '#10b981',
  },
  number_theory: {
    name: 'Number Theory',
    emoji: '∞',
    description: 'Even/odd, divisibility, integer properties',
    color: '#f59e0b',
  },
};
